// shop-mode.js — pure helpers for Shop Mode (the in-store shopping trip).
//
// Shop Mode is the mirror of Reset Mode: Reset pulls the world into the app,
// Shop pushes it back out — and because checking an item off restocks the pantry
// (store.completeShoppingItem), shopping doubles as intake. This file owns the
// pure shaping (aisle grouping, trip counts, dedupe keys, export text); the trip
// UI is src/modules/Shop.jsx and the feeders/commit live in useStore.
//
// v1 scope (open-questions §H): self-building list from depletion (hit-zero) +
// the existing Rebuy/toss path → category-grouped check-off → buy→inventory via
// completeShoppingItem → export. Recipe-gap feeder, min-keep thresholds, and
// pack-size/unit math are explicitly v2/v3.

import { CATEGORY_META } from "./pantry";

// Store-aisle order for the in-store checklist. Grouping is by the linked sku's
// `category` (the richer 8-value taxonomy), NOT `location` — decision §H.
export const AISLE_ORDER = ["produce", "protein", "dairy", "frozen", "beverage", "pantry", "spice", "other"];

export const SHOP_SOURCE_LABEL = {
  manual: "added",
  recipe: "recipe",
  "meal-plan": "meal plan",
  expiring: "expiring",
  low: "low stock",
  "ai-low": "low stock",
  depleted: "ran out",
  "reset-toss": "tossed",
};

export function shopSourceLabel(s) {
  return SHOP_SOURCE_LABEL[s] || s || "added";
}

// Group pending items by their linked sku's category, in aisle order. Items with
// no sku (free-text, recipe-gap) fall into "other". Returns only non-empty groups.
export function groupByAisle(items, skus) {
  const skuById = new Map((skus || []).map((s) => [s.id, s]));
  const buckets = new Map();
  for (const item of items || []) {
    const sku = item.sku_id ? skuById.get(item.sku_id) : null;
    const cat = (sku?.category && CATEGORY_META[sku.category]) ? sku.category : "other";
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat).push(item);
  }
  return AISLE_ORDER
    .filter((cat) => buckets.has(cat))
    .map((cat) => ({
      category: cat,
      label: CATEGORY_META[cat]?.label || "Other",
      icon: CATEGORY_META[cat]?.icon || "📦",
      items: buckets.get(cat),
    }));
}

export function tripCounts(items) {
  const list = items || [];
  const checked = list.filter((i) => i.done).length;
  return { total: list.length, checked, remaining: list.length - checked };
}

// Dedupe key for merge-by-product (v1 requirement — two feeders must not create
// duplicate lines). Prefer the sku_id; fall back to normalized text.
export function shoppingKey(item) {
  if (item?.sku_id) return `sku:${item.sku_id}`;
  return `txt:${String(item?.text || "").trim().toLowerCase()}`;
}

// Find an existing PENDING (not-done) list item that the incoming one should
// merge into, or null. `incoming` is { sku_id?, text? }.
export function findDuplicateShoppingItem(list, incoming) {
  const key = shoppingKey(incoming);
  return (list || []).find((i) => !i.done && shoppingKey(i) === key) || null;
}

// Best-effort quantity merge. Quantities are free-text; if both sides are plain
// numbers, sum them, otherwise keep whichever we have (existing wins). Real
// pack-size/unit reconciliation is deferred to v3 (§H).
export function bumpedQuantity(existingQty, addQty) {
  const a = parseFloat(String(existingQty ?? "").replace(/[^0-9.]/g, ""));
  const b = parseFloat(String(addQty ?? "").replace(/[^0-9.]/g, ""));
  const aNum = String(existingQty ?? "").trim() !== "" && !Number.isNaN(a);
  const bNum = String(addQty ?? "").trim() !== "" && !Number.isNaN(b);
  if (aNum && bNum) return String(a + b);
  return existingQty ?? addQty ?? null;
}

// Grouped, human-readable export of the list (share / paste elsewhere).
export function shoppingListText(items, skus) {
  const groups = groupByAisle((items || []).filter((i) => !i.done), skus);
  if (!groups.length) return "";
  const lines = ["Shopping list:"];
  for (const g of groups) {
    lines.push(`\n${g.icon} ${g.label}`);
    for (const it of g.items) {
      lines.push(`  ☐ ${it.text}${it.quantity ? ` ×${it.quantity}` : ""}`);
    }
  }
  return lines.join("\n");
}
